import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import ReactTooltip from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { themeGet } from '@styled-system/theme-get';

import SharingQrCode from '../../../../shared/SharingQrCode';
import Button from '../../../../shared/Button';
import SpouseButton from './EditSpouse.jsx';
import {
  Title,
  Label,
  UrlInput,
  UrlPrefix,
  UrlWrapper,
  Information,
  ToggleDiv,
  Box,
  ErrorMessage,
} from '../StyledComponents';
import ToggleSwitch from '../../../../shared/FormStyles/ToggleSwitch.jsx';
import Tooltip from '../../../../shared/Tooltip/Tooltip.jsx';
import GuestAccessSettings from './GuestAccessSettings';

const URL_PREFIX = `${import.meta.env.VITE_APP_HOST}/board/`;

const Sharing = ({
  demo,
  handleChange,
  inputError,
  handleBlur,
  setFieldValue,
  setFieldError,
  setFieldTouched,
}) => {
  const [copyLinkClicked, setCopyLinkClicked] = useState(false);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const { t } = useTranslation('boardSetup');

  const tooltipContent = copyLinkClicked
    ? t('sharing.copied')
    : t('sharing.clickToCopy');

  const boardUrl = `https://${URL_PREFIX}${demo.publicSlug}`;
  const toogleLabel = demo.isPublic
    ? t('sharing.enabled')
    : t('sharing.disabled');

  return (
    <div>
      <Tooltip
        id="copyLink"
        getContent={() => tooltipContent}
        afterHide={() => setCopyLinkClicked(false)}
      />
      <Box>
        <Title>{t('sharing.publicSharing')}</Title>
        <Information
          className="fa-regular fa-circle-info fa-xs"
          data-tip={t('sharing.titleHint')}
        />
        <ToggleDiv>
          <ToggleSwitch
            value={demo.isPublic}
            onChange={handleChange}
            label={toogleLabel}
            name="isPublic"
            id="public-board"
          />
        </ToggleDiv>
      </Box>
      {demo.isPublic && (
        <>
          <Label htmlFor="demo_url">
            {t('formLabel.publicSlug')}
            <Information
              className="fa-regular fa-circle-info fa-sm"
              data-tip={t('sharing.shareBoardDataTip')}
            />
          </Label>
          <MyWrapper>
            <UrlWrapper error={!!inputError('publicSlug')}>
              <UrlPrefix>{URL_PREFIX}</UrlPrefix>
              <UrlInput
                id="demo_url"
                type="text"
                name="publicSlug"
                onChange={handleChange}
                value={demo.publicSlug || ''}
                disabled={!demo.isPublic && 'disabled'}
                onBlur={handleBlur}
              />
              {inputError('publicSlug') && (
                <ErrorMessage>{inputError('publicSlug')}</ErrorMessage>
              )}
            </UrlWrapper>
            <ButtonsWrapper>
              <CopyToClipboard text={boardUrl}>
                <Button
                  mode="outline"
                  variant="primary"
                  minWidth="95px"
                  height="auto"
                  data-for="copyLink"
                  padding="5px 10px"
                  data-tip
                  onClick={() => setCopyLinkClicked(true)}
                >
                  {t('sharing.copyLink')}
                </Button>
              </CopyToClipboard>
              <SharingQrCode
                boardName={demo.name}
                isDemoPublic={demo.isPublic}
                boardUrl={boardUrl}
              />
            </ButtonsWrapper>
          </MyWrapper>
          <GuestAccessSettings
            settings={{
              hideWelcomeModalSignIn: demo.hideWelcomeModalSignIn,
              guestUserConversionModal: demo.guestUserConversionModal,
              useEmployeeId: demo.useEmployeeId,
              employeeIdHeader: demo.employeeIdHeader,
              employeeIdBody: demo.employeeIdBody,
            }}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            handleBlur={handleBlur}
            inputError={inputError}
            setFieldError={setFieldError}
            setFieldTouched={setFieldTouched}
          />
        </>
      )}
      {!demo.isPublic && (
        <DisabledMessage>
          <p
            dangerouslySetInnerHTML={{ __html: t('sharing.disabledMessage') }}
          />
        </DisabledMessage>
      )}
      <SpouseAccess>
        <SharingLabel>{t('sharing.spouseAccess')}</SharingLabel>
        <Information
          className="fa-regular fa-circle-info fa-xs"
          data-tip={t('sharing.spouseDataTip')}
          data-offset="{ 'right': 125 }"
        />
        {demo.dependentBoardEnabled && <SpouseButton demo={demo} />}
        <ToggleDiv>
          <ToggleSwitch
            value={demo.dependentBoardEnabled}
            onChange={handleChange}
            label={t('sharing.allow')}
            name="dependentBoardEnabled"
            id="allow-spouse"
          />
        </ToggleDiv>
      </SpouseAccess>
    </div>
  );
};

export default Sharing;

const SpouseAccess = styled(Box)`
  margin: 24px 0 0;
`;

const MyWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-direction: column;
  ${themeGet('mediaQueries.forExtraLargeUp')} {
    align-items: center;
    flex-direction: row;
    gap: 10px;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SharingLabel = styled(Label)`
  margin: 0;
`;

const DisabledMessage = styled.div`
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 24px;
  margin-top: 16px;

  p {
    color: #6c757d;
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
  }
`;
